import React, { useEffect, useState } from 'react';
import styles from './index.module.less';
import { useTranslation } from 'next-i18next';
import Image from 'next/image';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { InstagramAnonymouslyModel } from '@/types/anonymously.interface';
import { ga_logEvent } from '@/shared/ga';
import { EventName } from '@/shared/event-types';
import { Button } from 'antd';
import { useUserProvider } from '@/context/UserProvider';
import { fetchInstagramAnonymouslyTimesData } from '@/lib/service/anonymously';
import { SearchLimitLeftModel } from '@/types/home';
import { useRouter } from 'next/router';
import { demoData } from '@/lib/utils/demoData';
import { cn } from '@/lib/utils';

const LocalImage = dynamic(() => import('@/components/Common/LocalImage'), { ssr: true });
const InstagramStoryContent = dynamic(() => import('@/components/MainDashboardContent/InstagramStoryContent'), {
  ssr: true,
});

const SearchResultContent = ({
  locked = false,
  loading = false,
  loadMoreNotCheckLogin,
  post_page_token,
  tabFunc,
  data,
  onAddAccountToTrackClick,
  onPlanClick,
  onUnlock,
  isDemoData,
}: {
  locked?: boolean;
  loading?: boolean;
  loadMoreNotCheckLogin?: boolean;
  post_page_token?: string;
  tabFunc?: 'story' | 'highlights' | 'post';
  data?: InstagramAnonymouslyModel;
  onAddAccountToTrackClick?: (media_name?: string) => void;
  onPlanClick?: () => void;
  onUnlock?: () => void;
  isDemoData?: boolean;
}) => {
  const { t } = useTranslation();
  const { isLogin, onOpenLoginModal, onChangedLoginView } = useUserProvider();
  const [leftModel, setLeftModel] = useState<SearchLimitLeftModel>(null);
  const router = useRouter();

  useEffect(() => {
    if (isLogin) {
      fetchInstagramAnonymouslyTimesData(null).then((res) => {
        setLeftModel(res?.data);
      });
    } else {
      setLeftModel(null);
    }
  }, [isLogin]);

  const onDownloadClick = () => {
    ga_logEvent(EventName.GA_one_click_clicked);

    if (isLogin) {
      if (!leftModel.un_limit) {
        onPlanClick && onPlanClick();
      }
    } else {
      onChangedLoginView(false);
      onOpenLoginModal(true);
    }
  };

  const handleButtonClick = () => {
    if (isLogin) {
      if (data?.has_add) {
        router.push('/dashboard?dashboard_type=toolkit');
      } else {
        onAddAccountToTrackClick(data?.media_name);
      }
    } else {
      onChangedLoginView(false);
      onOpenLoginModal(true);
    }
  };

  return (
    <div className={`${styles.content} scroll-mt-20`} id="search-result-content">
      <div className={styles.anonymousView}>
        <span style={{ color: '#24A029' }}>{t('Anonymous view')}</span>
        <Image src="/images/freetools/warranty-icon.webp" alt={''} width={20} height={20} />
      </div>

      <div className={styles.bottomContainer}>
        <div className={styles.infContainer}>
          <LocalImage
            src={data?.avatar}
            style={{
              width: '80px',
              height: '80px',
              minWidth: '80px',
              borderRadius: '50%',
              overflow: 'hidden',
            }}
          />
          <div className={styles.info}>
            <div className={styles.name}>{data?.media_name}</div>

            <div style={{ color: '#725CFF' }}>
              <Link href={data?.media_url ?? ''} target="__block" style={{ wordBreak: 'break-all' }}>
                {data?.media_url ?? ''}
              </Link>
            </div>
          </div>
        </div>

        {!(isLogin && leftModel?.un_limit) && (
          <div className={styles.downloadContainer}>
            <div className={styles.download} onClick={onDownloadClick}>
              <Image src={'/images/landingpage/download_icon_1.webp'} alt={''} width={18} height={18} />
              <span>{t('Download all')}</span>
            </div>
            <div className={styles.downloadContent}>
              <p>{t('👍 Download all content in one click')}</p>
              <p>
                {t(
                  'Download Stories, Highlights, Mentions, and Links in one click, and export the data to Excel for easy organization.'
                )}
              </p>
            </div>
          </div>
        )}

        <div style={{ marginTop: '32px' }}>
          <InstagramStoryContent
            locked={data?.trigger_limit}
            loadMoreNotCheckLogin={loadMoreNotCheckLogin}
            tabFunc={tabFunc}
            post_page_token={post_page_token}
            media_name={data?.media_name}
            stories={data?.trigger_limit ? demoData.stories : data?.stories}
            highlights_list={data?.trigger_limit ? demoData.highlights_list : data?.highlights_list}
            post_list={data?.trigger_limit ? demoData.post_list : data?.post_list}
            post_num={data?.post_num}
            hideTitle={true}
            onUnlock={onUnlock}
            isDemoData={isDemoData}
          />
        </div>

        {data?.has_add ? (
          <Link href="/dashboard?dashboard_type=toolkit" className="block w-fit mx-auto mt-12">
            <Button className={cn(styles.addAccountContainer, '!inline-block !mt-0')}>
              {t('Go to Dashboard to See More')}
            </Button>
          </Link>
        ) : (
          <Button className={styles.addAccountContainer} loading={loading} onClick={handleButtonClick}>
            <Image src={'/images/home/subscribe-icon-1.webp'} alt={'subscribe-icon'} width={22} height={22} />
            <span>{t('Add Account to Track')}</span>
          </Button>
        )}

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          {data?.has_add
            ? t('This account is already connected. Head over to your dashboard to explore more.')
            : t('Tracking data is not saved. Subscribe to keep disappearing Stories and content.')}
        </div>
      </div>
    </div>
  );
};

export default SearchResultContent;
